import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { UserProfileApi } from "@/openapi/generated";
import type { FamilyContactAddFormSchemaType } from "@/schemas";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

export const useFamilyContactsCreate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async ({
      id,
      userProfileData,
    }: {
      id: number;
      userProfileData: FamilyContactAddFormSchemaType;
    }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(UserProfileApi, accessToken);

      // Map form data to API request, excluding homePhone which is not in API
      const { homePhone, ...apiData } = userProfileData;

      return service.apiUserProfileIdContactPost({
        id: id,
        createUserContactRequest: {
          firstName: apiData.firstName,
          lastName: apiData.lastName,
          email: apiData.email,
          cellPhone: apiData.cellPhone,
          addressLine1: apiData.addressLine1 || null,
          sendRoboDials: false,
          sendRoboTexts: false,
          sendEmail: false,
          sendStatements: false,
        },
      });
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.FamilyContactsGet, variables.id, "all"],
      });
      toast({
        title: "Family contact created",
        description: "Family contact created successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create family contact";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
